/**
 * useAdminAuth Hook
 * 
 * Custom hook to check if the current user is authenticated as an admin.
 * Uses Cloudflare Access - checks if user can access admin endpoints.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from '../services/apiClient';
import { apiEndpoints } from '../config/environment';
import { ApiError } from '../services/apiClient';

interface AdminAuthStatus {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface AdminMeResponse {
  isAdmin: boolean;
  authenticated: boolean;
}

/**
 * Check if user is authenticated as admin by calling the dedicated /api/admin/me endpoint.
 * Cloudflare Access will block this if not authenticated.
 * 
 * IMPORTANT: Fails closed - only returns true after a successful 200 JSON response.
 */
const checkAdminAuth = async (): Promise<{ isAuthenticated: boolean; error: string | null }> => {
  console.log('[checkAdminAuth] Starting API call to:', apiEndpoints.admin.me);
  try {
    const response = await apiGet<AdminMeResponse>(
      apiEndpoints.admin.me,
      { 
        timeout: 3000, // Short timeout for quick check
        credentials: 'include', // Include cookies for Cloudflare Access
      }
    );
    
    // Only return authenticated=true if we got a valid JSON response with both flags true
    // This is the ONLY path that sets isAuthenticated to true
    if (response.isAdmin === true && response.authenticated === true) {
      return {
        isAuthenticated: true,
        error: null,
      };
    }
    
    // If response doesn't have both flags true, treat as not authenticated
    return {
      isAuthenticated: false,
      error: null,
    };
  } catch (error) {
    // Handle different error types
    if (error instanceof ApiError) {
      const status = error.status;
      const code = error.code;
      
      // Cloudflare Access redirect detected
      if (code === 'CLOUDFLARE_ACCESS_REDIRECT') {
        return {
          isAuthenticated: false,
          error: null, // Not an error, just not authenticated
        };
      }
      
      // 401/403 = not authenticated (clear case)
      if (status === 401 || status === 403) {
        return {
          isAuthenticated: false,
          error: null, // Not an error, just not authenticated
        };
      }
      
      // 5xx = server error (temporary, default to false for security)
      if (status >= 500) {
        return {
          isAuthenticated: false, // Fail closed - default to false for safety
          error: 'Server error - authentication status unknown',
        };
      }
      
      // Other 4xx errors - fail closed
      return {
        isAuthenticated: false,
        error: `Authentication check failed: ${error.message}`,
      };
    }
    
    // Network errors, timeouts, etc. - fail closed
    return {
      isAuthenticated: false, // Fail closed - default to false for safety
      error: error instanceof Error ? error.message : 'Network error - authentication status unknown',
    };
  }
};

/**
 * Custom hook for admin authentication status
 * 
 * @param options - Configuration options
 * @param options.enabled - Whether to check authentication (default: true)
 * @param options.refreshInterval - Optional interval to re-check auth status in ms (default: disabled)
 * 
 * @returns Admin authentication status with refetch function
 */
export const useAdminAuth = (options: {
  enabled?: boolean;
  refreshInterval?: number;
} = {}): AdminAuthStatus => {
  const { enabled = true, refreshInterval } = options;
  
  // FAIL CLOSED: Start with false, only set to true after successful 200 JSON response
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Keep track of last known authenticated state (only true values)
  // This prevents flicker on transient network errors for already-authenticated users
  const lastKnownAuthState = useRef<boolean | null>(null);

  const checkAuth = useCallback(async (): Promise<void> => {
    if (!enabled) {
      setIsLoading(false);
      setIsAuthenticated(false); // Default to false when disabled
      return;
    }

    console.log('[useAdminAuth] Starting auth check...');
    setIsLoading(true);
    setError(null);

    try {
      console.log('[useAdminAuth] Calling checkAdminAuth...');
      const result = await checkAdminAuth();
      console.log('[useAdminAuth] Auth check result:', result);
      
      // FAIL CLOSED: Only set to true if we got a clear authenticated response
      // Otherwise, default to false for security
      setIsAuthenticated(result.isAuthenticated);
      
      // Update last known state only if we got a clear authenticated result
      if (result.isAuthenticated) {
        lastKnownAuthState.current = true;
      } else {
        // Any non-authenticated result (including errors) should clear the state
        // This ensures buttons don't stay visible after auth fails
        lastKnownAuthState.current = false;
      }
      
      setError(result.error);
    } catch (err) {
      // Fallback error handling - FAIL CLOSED
      const errorMessage = err instanceof Error ? err.message : 'Failed to check authentication';
      setError(errorMessage);
      
      // CRITICAL: On ANY error (including redirect errors), set to false
      // Do NOT preserve authenticated state on errors - this causes buttons to stay visible
      // The only way to be authenticated is a successful 200 JSON response
      setIsAuthenticated(false);
      lastKnownAuthState.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    console.log('[useAdminAuth] useEffect triggered - enabled:', enabled);
    // Initial check
    checkAuth();

    // Set up periodic refresh only if explicitly enabled
    let intervalId: NodeJS.Timeout | null = null;
    if (enabled && refreshInterval && refreshInterval > 0) {
      intervalId = setInterval(checkAuth, refreshInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkAuth, enabled, refreshInterval]);

  return {
    isAuthenticated,
    isLoading,
    error,
    refetch: checkAuth,
  };
};
